import React, { useState, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Dimensions,
  TouchableOpacity,
  FlatList,
  Image,
  Animated,
  StatusBar,
  SafeAreaView,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

const { width, height } = Dimensions.get('window');

type RootStackParamList = {
  SignUp: undefined;
  SignIn: undefined;
};

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

const slides = [
  {
    id: '1',
    title: 'Welcome to Final Key',
    description: 'Your secure digital vault for managing end-of-life planning and important documents.',
    image: require('../../assets/welcomesign.png'),
  },
  {
    id: '2',
    title: 'Organize Everything',
    description: 'Keep track of all your important information, from medical records to loyalty points, in one secure place.',
    image: require('../../assets/welcomesign2.png'),
  },
  {
    id: '3',
    title: 'Share Securely',
    description: 'Choose what to share and with whom. Your information stays private until you decide otherwise.',
    image: require('../../assets/welcomesign3.png'),
  },
];

export const OnboardingScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollX = useRef(new Animated.Value(0)).current;
  const slidesRef = useRef<FlatList>(null);

  const viewableItemsChanged = useRef(({ viewableItems }: any) => {
    setCurrentIndex(viewableItems[0]?.index || 0);
  }).current;

  const viewConfig = useRef({ viewAreaCoveragePercentThreshold: 50 }).current;

  const scrollTo = () => {
    if (currentIndex < slides.length - 1) {
      slidesRef.current?.scrollToIndex({
        index: currentIndex + 1,
        animated: true,
      });
    } else {
      navigation.replace('SignUp');
    }
  };

  const renderSlide = ({ item }: { item: typeof slides[0] }) => {
    return (
      <View style={styles.slide}>
        <View style={styles.imageContainer}>
          <Image 
            source={item.image} 
            style={styles.image} 
            resizeMode="contain"
          />
        </View>
        <View style={styles.contentBox}>
          <View style={styles.textContainer}>
            <Text style={styles.title}>{item.title}</Text>
            <Text style={styles.description}>{item.description}</Text>
          </View>
          
          <View style={styles.paginationContainer}>
            <View style={styles.paginationDots}>
              {slides.map((_, index) => {
                const inputRange = [
                  (index - 1) * width,
                  index * width,
                  (index + 1) * width,
                ];

                const dotWidth = scrollX.interpolate({
                  inputRange,
                  outputRange: [10, 20, 10],
                  extrapolate: 'clamp',
                });

                const opacity = scrollX.interpolate({
                  inputRange,
                  outputRange: [0.3, 1, 0.3],
                  extrapolate: 'clamp',
                });

                return (
                  <Animated.View
                    style={[
                      styles.dot,
                      { width: dotWidth, opacity },
                    ]}
                    key={index}
                  />
                );
              })}
            </View>
          </View>

          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={styles.skipButton}
              onPress={() => navigation.replace('SignUp')}
            >
              <Text style={styles.skipButtonText}>Skip</Text>
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.nextButton} 
              onPress={scrollTo}
            >
              <Text style={styles.nextButtonText}>
                {currentIndex === slides.length - 1 ? 'Get Started' : 'Next'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="dark-content" backgroundColor="#FFFFFF" />
      <View style={styles.flatListContainer}>
        <FlatList
          data={slides}
          renderItem={renderSlide}
          horizontal
          showsHorizontalScrollIndicator={false}
          pagingEnabled
          bounces={false}
          keyExtractor={(item) => item.id}
          onScroll={Animated.event(
            [{ nativeEvent: { contentOffset: { x: scrollX } } }],
            { useNativeDriver: false }
          )}
          onViewableItemsChanged={viewableItemsChanged}
          viewabilityConfig={viewConfig}
          ref={slidesRef}
        />
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  flatListContainer: {
    flex: 1,
  },
  slide: {
    width,
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  imageContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: ms(10),
  },
  image: {
    width: width * 0.8,
    height: height * 0.4,
  },
  contentBox: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(30),
    width: '85%',
    marginHorizontal: ms(30),
    marginBottom: ms(50),
    paddingHorizontal: ms(24),
    paddingTop: ms(32),
    paddingBottom: ms(50),
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  textContainer: {
    alignItems: 'center',
    marginBottom: ms(24),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    textAlign: 'center',
    marginBottom: ms(12),
  },
  description: {
    fontSize: ms(16, 0.3),
    color: '#62656b',
    textAlign: 'center',
    lineHeight: ms(24, 0.3),
  },
  paginationContainer: {
    marginBottom: ms(32),
  },
  paginationDots: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  dot: {
    height: s(8),
    borderRadius: ms(4),
    backgroundColor: '#29BFC0',
    marginHorizontal: ms(4),
  },
  buttonContainer: {
    alignItems: 'center',
    position: 'relative',
    width: '100%',
  },
  skipButton: {
    padding: ms(15),
    position: 'absolute',
    right: 0,
    bottom: ms(-50),
  },
  skipButtonText: {
    fontSize: ms(16, 0.3),
    color: '#62656b',
    fontWeight: '500',
  },
  nextButton: {
    backgroundColor: '#29BFC0',
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    borderRadius: ms(25),
    minWidth: s(120),
    alignItems: 'center',
  },
  nextButtonText: {
    fontSize: ms(16, 0.3),
    color: '#FFFFFF',
    fontWeight: '600',
  },
});

export default OnboardingScreen; 